import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MdsIcon } from '@mds/icons';
import { MakersiteService } from '@services/makersite.service';
import { forkJoin, Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { Report } from '@utils/reports';
import { AppsMenuComponent } from '@components/menus/apps-menu/apps-menu';
import { MenuService } from '@services/menu.service';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, AppsMenuComponent],
  selector: 'app-report-menu',
  templateUrl: './report-menu.component.html',
  styleUrls: ['./report-menu.component.scss']
})
export class ReportListComponent {
  private makersiteService = inject(MakersiteService);
  private menuService = inject(MenuService);
  private router = inject(Router);

  @Input() activeApp = null;

  private reload$ = new Subject<void>();
  protected menuConfig$ = this.menuService.menuConfig$.pipe(tap(() => this.reload$.next()));
  fetchReports$ = this.reload$.pipe(
    startWith(true),
    switchMap(() =>
      forkJoin({
        allReports: this.makersiteService.searchAvailableReports(),
        savedReports: this.makersiteService.getAllSavedReportTemplates()
      })
    ),
    map((res) => {
      const mappedSavedReports = this.mapReports(res.savedReports);
      const mappedAllReports = this.mapReports(res.allReports);

      return [
        {
          items: [{ items: mappedSavedReports, title: '' }],
          title: 'My templates',
          limit: 3,
          enableFavouriteButton: false,
          isFavorite: true
        },
        {
          items: this.groupReportsByLetter(mappedAllReports),
          title: 'All reports',
          enableFavouriteButton: false,
          isFavorite: true,
          showSubGroupTitles: true
        }
      ];
    })
  );

  private mapReports(reports) {
    return this.mapReportsToAppItem(this.sortReportsByTitle(reports.map((report) => this.removeReportPrefix(report))));
  }

  private removeReportPrefix(report) {
    return {
      ...report,
      rid: report.original_rid ? `template?${report.original_rid.replace('#', '')}` : report.rid, // report.original_rid is used for templates
      description: report.description.replace('Report Template | ', '')
    };
  }

  private groupReportsByLetter(allReports: Report[]) {
    if (!allReports.length) {
      return null;
    }

    const groupedReports: { [key: string]: Report[] } = {};

    allReports.forEach((report) => {
      const title = report.title ?? report.app_name;
      const firstLetter = title.charAt(0).toUpperCase();
      if (!groupedReports[firstLetter]) {
        groupedReports[firstLetter] = [];
      }
      groupedReports[firstLetter].push(report);
    });

    return Object.entries(groupedReports).map(([letter, reports]) => ({
      title: letter,
      items: reports
    }));
  }

  private getReportIcon(path: string): MdsIcon {
    const parsedPath = path.replaceAll(' ', '_').toUpperCase();
    return MdsIcon[`REPORT_${parsedPath}`] ?? MdsIcon.REPORT_EMPTY_CATEGORY;
  }

  private sortReportsByTitle(reports: Report[]): Report[] {
    return reports.sort((a, b) => a.title.localeCompare(b.title));
  }

  openReport(e): void {
    if (e.rid.includes('?')) {
      const id = e.rid.split('?')[1];
      this.router.navigate(['/report/', 'template'], { queryParams: { id } });
    } else {
      this.router.navigate(['/report/', e.rid]);
    }
  }

  mapReportsToAppItem(reports: Report[]): any[] {
    return reports.map((r) => {
      return {
        app_name: r.title ?? '',
        description_short: r.description ?? '',
        description_solution: r.description ?? '',
        category: r.report_category,
        icon_app: this.getReportIcon(r.report_category),
        rid: r.rid
      };
    });
  }
}
